import { useCallback } from "react";

import { useSaveCatalogBlock, useSaveCatalogPosition } from "./useInvoices";
import { errorText, type WebInvoiceLine } from "../lib/invoices";
import { katalogBlockAusGruppe, type Zeilengruppe } from "../lib/invoiceBlocks";

/**
 * Positionen und Blöcke in die gemeinsame Bibliothek schreiben.
 *
 * Aus `InvoicePositionEditor` herausgelöst (Größengrenze der Code-Standards);
 * die beiden Wege gehören ohnehin zusammen — sie schreiben in dieselbe
 * `positions_catalog.yaml`, die auch der Desktop liest.
 *
 * Die Rückmeldung geht über `melden` an die Oberfläche zurück, statt hier
 * einen eigenen Zustand zu halten: der Editor hat schon eine Notizzeile, und
 * zwei Meldungswege nebeneinander widersprechen sich irgendwann.
 */
export function useBibliothekSchreiben(melden: (text: string) => void) {
  const position = useSaveCatalogPosition();
  const block = useSaveCatalogBlock();

  const positionSchreiben = useCallback(async (line: WebInvoiceLine) => {
    const titel = line.title.trim();
    if (!titel) {
      melden("Die Position braucht einen Titel, bevor sie in die Bibliothek kann.");
      return;
    }
    try {
      await position.mutateAsync({
        title: titel,
        description: line.description.trim(),
        unit_label: line.unit_label || "Stück",
        unit_code: line.unit_code || "C62",
        unit_price_net: Number(line.unit_price_net) || 0,
        vat_rate_percent: line.vat_rate_percent,
      });
      melden(`„${titel}“ steht jetzt in der Bibliothek.`);
    } catch (fehler) {
      melden(errorText(fehler));
    }
  }, [position, melden]);

  const blockSchreiben = useCallback(async (gruppe?: Zeilengruppe) => {
    const titel = gruppe?.block?.title.trim();
    if (!titel) {
      melden("Der Block braucht eine Überschrift, bevor er in die Bibliothek kann.");
      return;
    }
    try {
      await block.mutateAsync(katalogBlockAusGruppe(gruppe!));
      melden(`Baustein „${titel}“ steht jetzt in der Bibliothek.`);
    } catch (fehler) {
      melden(errorText(fehler));
    }
  }, [block, melden]);

  return {
    positionSchreiben,
    blockSchreiben,
    laeuft: position.isPending || block.isPending,
  };
}
